import React, { useState, useEffect } from 'react';
import supabase from '../config/supabase';
import { Truck, Calculator, Layers } from 'lucide-react';

/**
 * 2-option Order with Session Toggle: On / Off
 * - Bootstrap CSS for styling (matches existing theme)
 * - Updates count session inventory_mode in database
 * - Compact header-friendly design
 * - REQUIRED: Must always have a value (defaults to 'Off')
 */

function InventoryModeToggle({ sessionId, initialMode = 'Off', onModeChange }) {
  // Ensure mode is always either 'Off' or 'On', defaulting to 'Off'
  const [mode, setMode] = useState(initialMode === 'On' ? 'On' : 'Off');
  const [isUpdating, setIsUpdating] = useState(false);

  const options = [
    {
      key: 'Off',
      label: 'Off',
      desc: 'Order with Session disabled',
      color: '#6c757d' // Bootstrap secondary color
    },
    {
      key: 'On',
      label: 'On',
      desc: 'Order with Session enabled',
      color: '#28a745' // Bootstrap success color
    }
  ];

  const updateOrderWithSession = async (newMode) => {
    if (!sessionId) return;

    setIsUpdating(true);
    try {
      // Set order_calculations_enabled based on the mode
      const orderCalculationsEnabled = newMode === 'On';

      const { data, error } = await supabase
        .from('count_sessions')
        .update({
          inventory_mode: orderCalculationsEnabled ? 'Both' : 'Order', // Map to database values
          order_calculations_enabled: orderCalculationsEnabled
        })
        .eq('id', sessionId)
        .select();

      if (error) {
        console.error('Supabase error updating order with session:', error);
        return;
      }

      setMode(newMode);
      if (onModeChange) {
        onModeChange(newMode, orderCalculationsEnabled);
      }

      console.log('Order with Session updated to:', newMode, 'order_calculations_enabled:', orderCalculationsEnabled);
    } catch (error) {
      console.error('Error updating order with session:', error);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleModeChange = (newMode) => {
    console.log('🔄 [ORDER-WITH-SESSION] Click detected:', newMode, 'Current mode:', mode);

    // Ensure newMode is valid (either 'Off' or 'On')
    if (newMode !== 'Off' && newMode !== 'On') {
      console.warn('⚠️ [ORDER-WITH-SESSION] Invalid mode:', newMode, 'defaulting to Off');
      newMode = 'Off';
    }

    if (newMode !== mode && !isUpdating) {
      console.log('🔄 [ORDER-WITH-SESSION] Mode change triggered:', mode, '->', newMode);

      // For new sessions (no sessionId), just update local state
      if (!sessionId) {
        const orderCalculationsEnabled = newMode === 'On';
        setMode(newMode);
        if (onModeChange) {
          onModeChange(newMode, orderCalculationsEnabled);
        }
        console.log('✅ [ORDER-WITH-SESSION] Local mode updated to:', newMode, 'order_calculations_enabled:', orderCalculationsEnabled);
      } else {
        // For existing sessions, update database
        updateOrderWithSession(newMode);
      }
    } else {
      console.log('⚠️ [ORDER-WITH-SESSION] Click ignored - same mode or updating');
    }
  };

  return (
    <div className="d-flex flex-column align-items-center">
      {/* Toggle buttons */}
      <div
        className="d-flex rounded-pill p-1"
        style={{
          backgroundColor: '#f8f9fa',
          border: '1px solid #dee2e6',
          maxWidth: '150px'
        }}
        role="radiogroup"
        aria-label="Order with Session Toggle"
      >
        {options.map(({ key, label, desc, color }) => {
          const active = mode === key;
          return (
            <button
              key={key}
              type="button"
              role="radio"
              aria-checked={active}
              onClick={() => handleModeChange(key)}
              disabled={isUpdating}
              title={desc}
              className="btn btn-sm position-relative border-0"
              style={{
                fontSize: '0.7rem',
                padding: '6px 12px',
                minWidth: '50px',
                borderRadius: '20px',
                backgroundColor: active ? '#ffffff' : 'transparent',
                color: active ? color : '#6c757d',
                boxShadow: active ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                transition: 'all 0.2s ease',
                margin: '1px'
              }}
            >
              {/* Label only */}
              <span style={{ fontSize: '0.7rem', fontWeight: active ? '600' : '400' }}>
                {label}
              </span>

              {/* Loading indicator */}
              {isUpdating && active && (
                <div className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center rounded-pill"
                     style={{ backgroundColor: 'rgba(255,255,255,0.8)' }}>
                  <div className="spinner-border" role="status" style={{ width: '12px', height: '12px' }}>
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              )}
            </button>
          );
        })}
      </div>

      {/* Title below toggle buttons */}
      <small
        className="text-muted text-center"
        style={{
          fontSize: '0.7rem',
          whiteSpace: 'nowrap',
          fontWeight: 'bold',
          marginTop: '3px'
        }}
      >
        Order with Session:
      </small>
    </div>
  );
}

// Export the component
export default InventoryModeToggle;

// Make component available globally for HTML integration
if (typeof window !== 'undefined') {
  window.InventoryModeToggle = InventoryModeToggle;
}

// Usage example for integration:
/*
<InventoryModeToggle
  sessionId={currentSessionId}
  initialMode={session.inventory_mode || 'Order'}
  onModeChange={(newMode) => {
    console.log('Inventory mode changed to:', newMode);
    // Update any dependent UI or calculations
  }}
/>
*/
